import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Loader2, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";

const SUGGESTIONS = [
  "How do I get a Vietnam e-visa?",
  "How long does business registration take?",
  "Can I extend my visa without leaving Vietnam?",
];

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const fmtMd = (t) => {
  const lines = esc(t).split("\n");
  let html = "", inList = false;
  for (const line of lines) {
    const b = line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");
    const m = b.match(/^\s*[-*]\s+(.*)/);
    if (m) { if (!inList) { html += "<ul>"; inList = true; } html += `<li>${m[1]}</li>`; }
    else { if (inList) { html += "</ul>"; inList = false; } if (b.trim()) html += `<p>${b}</p>`; }
  }
  if (inList) html += "</ul>";
  return html;
};

const GREETING = { role: "assistant", content: "Hi! I'm the ImmiLaw Assistant 👋 Ask me anything about Vietnam visas, business registration, extensions or overstay reports." };

export const Assistant = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.filter((m) => m !== GREETING);
    const next = [...messages, { role: "user", content: msg }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, history });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: apiErr(e, "Sorry, I'm having trouble right now. Please contact us at info@immilaw-vn.com.") }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed bottom-5 left-5 z-50 flex flex-col items-start gap-3">
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 16, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.95 }}
            className="bg-white rounded-3xl shadow-lift border border-slate-200 w-[92vw] sm:w-96 flex flex-col overflow-hidden" style={{ height: "min(560px, 72vh)" }} data-testid="assistant-panel">
            <div className="bg-brand-navy px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-brand-red flex items-center justify-center"><Bot className="w-5 h-5 text-white" /></div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">ImmiLaw Assistant</p>
                  <p className="text-[11px] text-slate-400">AI immigration helper</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="assistant-close"><X className="w-5 h-5" /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "user" ? (
                    <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap bg-brand-red text-white">{m.content}</div>
                  ) : (
                    <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-white border border-slate-200 text-brand-navy [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:my-1 [&_p]:mb-1.5 [&_p:last-child]:mb-0 [&_li]:mb-0.5"
                      dangerouslySetInnerHTML={{ __html: fmtMd(m.content) }} />
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 text-brand-red animate-spin" /></div></div>
              )}
              {messages.length <= 1 && !loading && (
                <div className="pt-2 space-y-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="assistant-suggestion"
                      className="w-full text-left text-xs px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-600 hover:border-brand-red hover:text-brand-red transition-colors flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5 text-brand-gold" /> {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-100 flex gap-2 bg-white">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about Vietnam immigration…" data-testid="assistant-input"
                className="flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand-red" />
              <button type="submit" disabled={loading} data-testid="assistant-send"
                className="w-11 h-11 rounded-full bg-brand-red text-white flex items-center justify-center hover:bg-brand-reddark transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <button onClick={() => setOpen((v) => !v)} aria-label="AI Assistant" data-testid="assistant-fab"
        className="w-14 h-14 rounded-full bg-brand-navy text-white shadow-lift flex items-center justify-center hover:scale-105 transition-transform relative">
        <Bot className="w-7 h-7" />
        {!open && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-brand-gold border-2 border-white" />}
      </button>
    </div>
  );
};
